import { useQuery, useMutation } from "@apollo/client";
import { Button, Typography,Box, Dialog, DialogActions, DialogContent, DialogContentText, IconButton, Tooltip } from "@mui/material";
import { ColumnsType } from "antd/es/table";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux"; // Or your state management library
import { Link, useNavigate } from "react-router-dom";
import Card from "../../Components/Cards/Card";
import DataTable from "../../Components/Datatable";
import Stats from "../../Components/Stats";
import TableHeading from "../../Components/TableHeading";
import globals from "../../Constants/Globals";
import { GET_TRAINING_MODULE, GET_USER_BY_ID, GET_ORG_BY_ID } from "../../Graphql/Queries";
import { SuperAdminPaths } from "../../Routes/Paths";
import { pages } from "../../Types/DatatableType";
import DeleteIcon from '@mui/icons-material/Delete';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye'; // Import the View Details icon
import NoteAddIcon from '@mui/icons-material/NoteAdd'; // Import the Create icon
import { Error } from '../../Types/MessageType';
import Message from '../../Components/Message';
import { DELETE_TRAINING_PACKAGE } from "../../Graphql/Mutations";

const TrainingPackage = () => {
  const { CREATE_TRAINING_PACKAGE } = SuperAdminPaths;

  const [tableData, setTableData] = useState<any[]>([]);
  const [totalPages, setTotalPages] = useState<pages>();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isRootAdmin, setIsRootAdmin] = useState<boolean>(false);
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [alert, setAlert] = useState<Error>({ type: 'warning', message: '' });
  const [open, setOpen] = useState(false);
  const [record, setRecord] = useState<any>(null);
  const [totalTrainingPackages, setTotalTrainingPackages] = useState<number>(0);
  const [deleteTrainingPackage] = useMutation(DELETE_TRAINING_PACKAGE);
  // Replace this with your own way of getting the user ID
  const userId = useSelector((state: any) => state.auth.userId);
  const navigate = useNavigate();

  // Fetch the user's role and organization
  const { data: userData, loading: userLoading } = useQuery(GET_USER_BY_ID, {
    variables: { userWithRolesByIdId: userId },
    onCompleted(data) {
      const roles = data?.userWithRolesById?.roles || [];
      const isAdmin = roles.some((role: any) => role.roleCode === "ROOT_ADMIN");
      setIsRootAdmin(isAdmin);
      const orgId = data?.userWithRolesById?.organization || null;
      setOrganizationId(orgId);
    },
  });

  const [searchCriteria, setSearchCriteria] = useState({
    name: '',
    totalTrainings: '',
    totalGroups: ''
  });
  const handleSearchChange = (field: string, value: string) => {
    setSearchCriteria(prev => ({
      ...prev,
      [field]: value
    }));
  };

  useEffect(() => {
    if (!userLoading && !isRootAdmin && organizationId) {
    }
  }, [userLoading, isRootAdmin, organizationId]);

  // Query for root admin
  const { loading: trainingLoading } = useQuery(GET_TRAINING_MODULE, {
    skip: !isRootAdmin,
    variables: {
      page: currentPage,
      limit: globals.pageSize,
      search: {
        name: searchCriteria.name,
        totalTrainings: searchCriteria.totalTrainings ? parseInt(searchCriteria.totalTrainings, 10) : null,
        totalGroups: searchCriteria.totalGroups ? parseInt(searchCriteria.totalGroups, 10) : null,
      }
    },
    onCompleted(data) {
      if (data.getAllTrainingPackages.trainingPackages.length > 0) {
        const trainingP = data?.getAllTrainingPackages?.trainingPackages;
        setTotalTrainingPackages(data.getAllTrainingPackages?.totalDocs);
        setTotalPages({
          currentPage: data.getAllTrainingPackages?.currentPage,
          limit: globals.pageSize,
          totalDocs: data.getAllTrainingPackages?.totalDocs,
          totalPages: data.getAllTrainingPackages?.totalPages,
        });
        setTableData([]);
        if (trainingP.length > 0) {
          const _trainingP = trainingP.map((_singleTP: any, index: number) => {
            return {
              key: index,
              id: _singleTP._id,
              trainingPackageName: _singleTP.name,
              totalTrainings: _singleTP.trainings.length,
            };
          });
          setTableData(_trainingP);
        }
      }
    },
  });

  const filteredData = tableData.filter(item => {
    const matchesName = item.trainingPackageName.toLowerCase().includes(searchCriteria.name.toLowerCase());

    const totalTrainingsMatch = 
      searchCriteria.totalTrainings === '' || 
      item.totalTrainings === parseInt(searchCriteria.totalTrainings, 10);

    const totalGroupsMatch = 
      searchCriteria.totalGroups === '' || 
      item.totalGroups === parseInt(searchCriteria.totalGroups, 10);

    return matchesName && totalTrainingsMatch && totalGroupsMatch;
  });

  // Query for non-root admin
  const { loading: orgLoading } = useQuery(GET_ORG_BY_ID, {
    skip: isRootAdmin || !organizationId,
    variables: { organizationByIdId: organizationId },
    onCompleted(data) {
      let trainingPackages = data?.organizationById?.trainingPackage || [];
      // Ensure trainingPackages is an array
      if (!Array.isArray(trainingPackages)) {
        trainingPackages = [trainingPackages];
      }
      setTotalPages({
        currentPage,
        limit: globals.pageSize,
        totalDocs: trainingPackages.length,
        totalPages: Math.ceil(trainingPackages.length / globals.pageSize),
      });
      const _trainingP = trainingPackages.map((_singleTP: any, index: number) => {
        return {
          key: index,
          id: _singleTP._id,
          trainingPackageName: _singleTP.name,
          totalTrainings: _singleTP.trainings?.length || 0,
        };
      });
      setTableData(_trainingP);
    },
  });

  const handleClickOpen = (record:any) => {
    setRecord(record);
     setOpen(true);
   };
  
   const handleDelete = () => {
     handleDeleteTrainingPackage(record);
     setOpen(false);
     setRecord(null);
   };
  
   const handleClose = ()=>{
    setOpen(false);
   }

  const handleDeleteTrainingPackage = (record: any): void => {
    deleteTrainingPackage({
      variables: {
        id: record.id
      },
      onCompleted(data) {
        const updatedTableData = tableData.filter(item => item.id !== record.id);
        setTableData(updatedTableData);
        setTotalTrainingPackages(totalTrainingPackages=>totalTrainingPackages - 1);
        setAlert({ type: "success", message: "Training package deleted successfully" });
        setTimeout(() => {
          setAlert({
            type: 'success',
            message: ''
          });
        }, globals.timeout);
      },
      onError(error) {
        setAlert({ type: 'error', message: error.message });
        setTimeout(() => {
          setAlert({
            type: 'error',
            message: ''
          });
        }, globals.timeout);
      }
    });
  };

  const columns: ColumnsType<any> = [
    {
      title: (
        <TableHeading heading="Package Name" setSearch={(value) => handleSearchChange('name', value)} setPage={setCurrentPage} />
      ),
      dataIndex: "trainingPackageName",
      render: (text) => <Typography variant="body1" sx={{ textAlign: 'left' }}>{text}</Typography>,
      key: "1",
      width: "30%",
    },
    {
      title: (
        <TableHeading heading="Total Trainings" setSearch={(value) => handleSearchChange('totalTrainings', value)} setPage={setCurrentPage} />
      ),
      dataIndex: "totalTrainings",
      render: (text) => <Typography variant="body1" sx={{ textAlign: 'left' }}>{text}</Typography>,
      key: "2",
    },
    {
      title: (
        <TableHeading heading="Total Groups" setSearch={(value) => handleSearchChange('totalGroups', value)} setPage={setCurrentPage} />
      ),
      dataIndex: "trainingModule",
      render: (text) => <Typography variant="body1" sx={{ textAlign: 'left' }}>{text}</Typography>,
      key: "3",
    },
    {
      title: '',
      render: (_, record) => (
        <Tooltip title="View Details" arrow>
          <IconButton
            sx={{ color: "rgb(250, 150, 157)" }}
            onClick={() => navigate(record.id, { state: { trainingPackageId: record.id } })}
          >
            <RemoveRedEyeIcon sx={{ fontSize: "1.8rem" }} />
          </IconButton>
        </Tooltip>
      ),
      key: '4',
      width: '20%',
    },
    {
      title: '',
      render: (_, record) => <IconButton
                                sx={{ alignSelf: 'flex-end' }}
                                onClick={(e) => {
                                  e.stopPropagation(); 
                                  handleClickOpen(record); 
                                }}>
                                    <DeleteIcon />
                              </IconButton>,
      key: "6",
      width: "5%"
     }
  ];

  const loading = userLoading || (isRootAdmin ? trainingLoading : orgLoading);

  const handleRowClick = (record: any) => {
    navigate(`/training-package/${record.id}`, { state: { trainingPackageId: record.id } });
  };

  return (
    <>
      <Stats
        buttonText={
          <>
            Create Package <NoteAddIcon sx={{ color: 'white', ml: 1 }} />
          </>
        }
        text="Total Training Package"
        number={totalTrainingPackages}
        link={CREATE_TRAINING_PACKAGE}
      />
      <Box flexGrow={1} sx={{ pr: 3 }}>
        {!!alert?.message ? <Message type={alert?.type} message={alert.message} variant={"filled"} /> : null}
      </Box>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this training package?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleDelete} autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      <Card>
        <DataTable
          loading={loading}
          tableData={filteredData}
          totalPages={totalPages}
          showCheck={false}
          columnData={columns}
          page={(cp) => {
            setCurrentPage(Number(cp));
          }}
          scrollX="max-content"
          scrollY={450}
          onRow={(record) => handleRowClick(record)}
        />
      </Card>
    </>
  );
};

export default TrainingPackage;
